import { refreshAccessToken } from '@/lib/http-client';
import { tokenStorage } from '@/lib/token-storage';

export interface AuthenticatedSseEvent {
  data: string;
  event: string;
}

export interface AuthenticatedSseOptions {
  onEvent: (event: AuthenticatedSseEvent) => void;
  onError?: (error: unknown) => void;
  retryDelayMs?: number;
}

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(
  /\/+$/,
  '',
);

const wait = (milliseconds: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const finish = () => {
      window.clearTimeout(timeout);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timeout = window.setTimeout(finish, milliseconds);
    signal.addEventListener('abort', finish, { once: true });
  });

export function connectAuthenticatedSse(
  path: string,
  { onEvent, onError, retryDelayMs = 3_000 }: AuthenticatedSseOptions,
): () => void {
  const controller = new AbortController();

  const connect = async () => {
    let retryDelay = retryDelayMs;
    let refreshedAfter401 = false;
    while (!controller.signal.aborted) {
      const authorization = tokenStorage.getAuthorizationHeader();
      if (!authorization) return;
      try {
        const response = await fetch(`${apiBaseUrl}${path}`, {
          headers: { Accept: 'text/event-stream', Authorization: authorization },
          signal: controller.signal,
        });
        if (response.status === 401 && !refreshedAfter401) {
          refreshedAfter401 = true;
          if (await refreshAccessToken()) continue;
          return;
        }
        // Retrying with the same credentials cannot succeed; without this the
        // stream reconnected with an expired token indefinitely.
        if ([401, 403, 404].includes(response.status)) {
          onError?.(new Error(`SSE request rejected with status ${response.status}`));
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`SSE request failed with status ${response.status}`);
        }
        retryDelay = retryDelayMs;
        refreshedAfter401 = false;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!controller.signal.aborted) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer = `${buffer}${decoder.decode(value, { stream: true })}`.replace(/\r\n?/g, '\n');
          const messages = buffer.split('\n\n');
          buffer = messages.pop() ?? '';
          for (const message of messages) {
            let event = 'message';
            const data: string[] = [];
            for (const line of message.split('\n')) {
              if (line.startsWith('event:')) event = line.slice(6).trim();
              if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
              if (line.startsWith('retry:')) {
                const candidate = Number.parseInt(line.slice(6).trim(), 10);
                if (Number.isFinite(candidate)) retryDelay = candidate;
              }
            }
            if (data.length) onEvent({ event, data: data.join('\n') });
          }
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        onError?.(error);
      }
      await wait(retryDelay, controller.signal);
      retryDelay = Math.min(retryDelay * 2, 30_000);
    }
  };

  void connect();
  return () => controller.abort();
}
